// Notification API service following updated backend documentation
import { AxiosInstance, AxiosResponse } from 'axios';
import { http, useApiWithOrg } from './index';
// Removed unused imports
import { Role } from '@/auth/constants';
import keycloak from '@/auth/keycloak';
import { useMemo } from 'react';

// User type for different API endpoints
export type UserType = 'internal' | 'parent';

// Data interfaces matching the backend documentation exactly
export interface NotificationData {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  userId: string;
  organizationId: string;
  metadata: Record<string, any>;
  createdAt: string;
  readAt: string | null;
  taskId?: string; // Optional field for task-related notifications
}

// Updated notification types from backend documentation
export type NotificationType =
  | 'event_change'
  | 'event_cancellation'
  | 'health_alert'
  | 'financial'
  | 'administrative'
  | 'security'
  | 'message'
  | 'announcement'
  | 'reminder'
  | 'task_assigned';

export interface ChannelPreferences {
  email: boolean;
  in_app: boolean;
  sms: boolean;
  push: boolean;
}

export interface GlobalChannelPreferences {
  email: boolean;    // Master toggle for all email notifications
  in_app: boolean;   // Master toggle for all in-app notifications  
  sms: boolean;      // Master toggle for all SMS notifications
  push: boolean;     // Master toggle for all push notifications
}

export interface TypeSpecificOverrides {
  [notificationType: string]: {
    email?: boolean;
    in_app?: boolean;
    sms?: boolean;
    push?: boolean;
  };
}

export interface NotificationPreferences {
  id: string;
  userId: string;
  organizationId: string;
  userType: 'internal' | 'parent';
  
  // NEW: Global master switches
  globalChannelPreferences: GlobalChannelPreferences;
  
  // NEW: Only contains exceptions to global defaults
  typeSpecificOverrides: TypeSpecificOverrides;
  
  // Settings
  isActive: boolean;
  quietHoursStart?: string; // "22:00"
  quietHoursEnd?: string;   // "08:00"
  timezone: string;         // "Europe/Belgrade"
  createdAt: string;
  updatedAt: string;
  
  // Legacy (for backward compatibility)
  preferences?: Record<NotificationType, ChannelPreferences>;
}

export interface NotificationHistoryResponse {
  notifications: NotificationData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface NotificationUnreadCountResponse {
  count: number;
}

export interface MarkAsReadResponse {
  success: boolean;
  readAt: string;
}

// API service class following updated backend documentation
export class NotificationAPIService {
  private api: AxiosInstance;
  private userType: UserType;

  constructor(apiInstance: AxiosInstance, userType: UserType = 'internal') {
    this.api = apiInstance;
    this.userType = userType;
  }

  // Get the base path for endpoints based on user type
  private getBasePath(): string {
    return this.userType === 'parent' ? '/parents/notifications' : '/notifications';
  }

  // Get notification preferences
  async getPreferences(): Promise<NotificationPreferences> {
    const endpoint = `${this.getBasePath()}/preferences`;
    const response: AxiosResponse<NotificationPreferences> = await this.api.get(endpoint);
    return response.data;
  }

  // Update notification preferences with new global/override structure
  async updatePreferences(updates: {
    globalChannelPreferences?: Partial<GlobalChannelPreferences>;
    typeSpecificOverrides?: Partial<TypeSpecificOverrides>;
    isActive?: boolean;
    quietHoursStart?: string;
    quietHoursEnd?: string;
    timezone?: string;
    // Legacy support
    preferences?: Partial<Record<NotificationType, ChannelPreferences>>;
  }): Promise<NotificationPreferences> {
    const endpoint = `${this.getBasePath()}/preferences`;
    const response: AxiosResponse<NotificationPreferences> = await this.api.put(endpoint, updates);
    return response.data;
  }

  // Helper method to update global channel preferences
  async updateGlobalPreferences(
    updates: Partial<GlobalChannelPreferences>, 
    currentGlobalPrefs: GlobalChannelPreferences
  ): Promise<NotificationPreferences> {
    // Merge current preferences with updates to avoid losing other values
    const mergedGlobalPrefs = {
      ...currentGlobalPrefs,
      ...updates
    };
    return this.updatePreferences({ globalChannelPreferences: mergedGlobalPrefs });
  }

  // Legacy method for backward compatibility (will make extra API call)
  async updateGlobalPreferencesLegacy(updates: Partial<GlobalChannelPreferences>): Promise<NotificationPreferences> {
    const currentPrefs = await this.getPreferences();
    return this.updateGlobalPreferences(updates, currentPrefs.globalChannelPreferences);
  }

  // Helper method to update type-specific overrides
  async updateTypeOverride(
    notificationType: NotificationType, 
    channelUpdates: { email?: boolean; in_app?: boolean; sms?: boolean; push?: boolean; }
  ): Promise<NotificationPreferences> {
    return this.updatePreferences({ 
      typeSpecificOverrides: { 
        [notificationType]: channelUpdates 
      } 
    });
  }

  // Get notification history with pagination and filtering
  async getNotifications(page = 1, limit = 20, type?: string): Promise<NotificationHistoryResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(type && { type }),
    });
    
    const endpoint = `${this.getBasePath()}?${params}`;
    const response: AxiosResponse<NotificationHistoryResponse> = await this.api.get(endpoint);
    return response.data;
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<MarkAsReadResponse> {
    const endpoint = `${this.getBasePath()}/${notificationId}/read`;
    const response: AxiosResponse<MarkAsReadResponse> = await this.api.put(endpoint);
    return response.data;
  }

  // Mark all notifications as read (not in backend doc, but keeping for compatibility)
  async markAllAsRead(): Promise<void> {
    const endpoint = `${this.getBasePath()}/read-all`;
    await this.api.put(endpoint);
  }

  // Get unread count
  async getUnreadCount(): Promise<NotificationUnreadCountResponse> {
    const endpoint = `${this.getBasePath()}/unread-count`;
    const response: AxiosResponse<NotificationUnreadCountResponse> = await this.api.get(endpoint);
    return response.data;
  }

  // Update user type
  setUserType(userType: UserType): void {
    this.userType = userType;
  }

  // Get current user type
  getUserType(): UserType {
    return this.userType;
  }
}

// Function to detect user type based on token or user roles
export const detectUserType = (): UserType => {
  try {
    const tokenParsed = keycloak.tokenParsed;
    if (!tokenParsed) {
      return 'internal'; // Default to internal if no token
    }

    // Check for parent role in direct roles
    if (tokenParsed.roles && Array.isArray(tokenParsed.roles)) {
      const isParent = tokenParsed.roles.includes(Role.Parent);
      return isParent ? 'parent' : 'internal';
    }

    // Check for parent role in organization roles
    if (tokenParsed.organizations) {
      const currentOrgId = localStorage.getItem('currentOrganizationId');
      if (currentOrgId && tokenParsed.organizations[currentOrgId]) {
        const orgRoles = tokenParsed.organizations[currentOrgId].roles || [];
        const isParent = orgRoles.includes(Role.Parent);
        return isParent ? 'parent' : 'internal';
      }
      
      // Check all organizations for parent role
      for (const orgData of Object.values(tokenParsed.organizations)) {
        if (orgData && typeof orgData === 'object' && 'roles' in orgData) {
          const roles = (orgData as any).roles || [];
          if (roles.includes(Role.Parent)) {
            return 'parent';
          }
        }
      }
    }

    return 'internal'; // Default to internal
  } catch (error) {
    console.error('Error detecting user type:', error);
    return 'internal'; // Safe default
  }
};

// Factory function to create API service instance with organization context
export const createNotificationAPIService = (useOrgContext = true, userType?: UserType): NotificationAPIService => {
  const apiInstance = useOrgContext ? useApiWithOrg() : http;
  const detectedUserType = userType || detectUserType();
  return new NotificationAPIService(apiInstance, detectedUserType);
};

// Hook for using notification API with organization context
export const useNotificationAPI = (userType?: UserType) => {
  const apiWithOrg = useApiWithOrg();
  const detectedUserType = userType || detectUserType();
  
  return useMemo(() => {
    return new NotificationAPIService(apiWithOrg, detectedUserType);
  }, [apiWithOrg, detectedUserType]);
};
